import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Profile } from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";

type Props = {
  initialName: string;
  fallbackPlaceholder: string;
  onContinue: () => void;
};

export function StepName({ initialName, fallbackPlaceholder, onContinue }: Props) {
  const [value, setValue] = useState(() => {
    const trimmed = initialName.trim();
    if (!trimmed) return "";
    if (trimmed.toLowerCase() === fallbackPlaceholder.toLowerCase()) return "";
    return trimmed;
  });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { run } = useApiCall();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = value.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= 80;

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    const ok = await run(() => Profile.updateDisplayName(trimmed));
    setBusy(false);
    if (ok) onContinue();
  }

  return (
    <section className="space-y-10">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-wider text-ink-3">
          Paso 1
        </p>
        <h1 className="text-5xl sm:text-7xl font-black tracking-tight text-ink leading-[1.05]">
          ¿Cómo quieres
          <br />
          que te llamemos?
        </h1>
        <p className="text-base sm:text-lg text-ink-2 max-w-xl">
          Lo usaremos para saludarte y nada más. Puedes cambiarlo cuando
          quieras.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-6"
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Tu nombre"
          maxLength={80}
          className="w-full bg-transparent text-3xl sm:text-5xl font-black tracking-tight text-ink placeholder:text-ink-3/40 border-0 border-b-2 pb-3 focus:outline-none transition-colors"
          style={{
            borderBottomColor: "rgba(15,15,15,0.18)",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderBottomColor = "#0F0F0F";
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderBottomColor = "rgba(15,15,15,0.18)";
          }}
          autoComplete="given-name"
          spellCheck={false}
        />

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={!valid || busy}
            className="inline-flex items-center gap-2 bg-ink text-white text-base font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:scale-[1.02] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {busy ? "Guardando…" : "Continuar"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <span className="text-xs text-ink-3">
            o pulsa <kbd className="px-1.5 py-0.5 rounded bg-surface-2 font-mono text-[11px]">Enter</kbd>
          </span>
        </div>
      </form>
    </section>
  );
}
